import React, { useState, useEffect } from "react";
import countryService from "../../services/countryService";

const CityForm = ({ onSubmit, onCancel, loading = false }) => {
  const [formData, setFormData] = useState({
    name: "",
    countryId: "",
  });
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    try {
      setCountriesLoading(true);
      const response = await countryService.getAllCountries();
      if (response.success) {
        setCountries(response.data || []);
      }
    } catch (error) {
      console.error("Error loading countries:", error);
    } finally {
      setCountriesLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "City name is required";
    }

    if (!formData.countryId) {
      newErrors.countryId = "Country selection is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Send exactly what the backend expects
      onSubmit({
        name: formData.name.trim(),
        countryId: parseInt(formData.countryId),
      });
    }
  };

  const selectedCountry = countries.find(
    (country) => country.id.toString() === formData.countryId
  );

  return (
    <div className="city-form">
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label fw-medium">
            City Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className={`form-control ${errors.name ? "is-invalid" : ""}`}
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Enter city name"
            disabled={loading}
          />
          {errors.name && <div className="invalid-feedback">{errors.name}</div>}
        </div>

        <div className="mb-3">
          <label className="form-label fw-medium">
            Country <span className="text-danger">*</span>
          </label>
          <select
            className={`form-select ${errors.countryId ? "is-invalid" : ""}`}
            name="countryId"
            value={formData.countryId}
            onChange={handleInputChange}
            disabled={loading || countriesLoading}
          >
            <option value="">
              {countriesLoading ? "Loading countries..." : "Select a country"}
            </option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {country.name}
              </option>
            ))}
          </select>
          {errors.countryId && (
            <div className="invalid-feedback">{errors.countryId}</div>
          )}
        </div>

        {/* Preview selected data */}
        {formData.name && selectedCountry && (
          <div className="mb-3">
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              <strong>Creating:</strong> {formData.name}, {selectedCountry.name}
            </div>
          </div>
        )}

        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-info"
            disabled={loading || countriesLoading}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                ></span>
                Creating...
              </>
            ) : (
              <>
                <i className="fas fa-plus me-2"></i>
                Create City
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CityForm;
